import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import notificationService from '../services/notificationService';
import pushTokenService from '../services/pushTokenService';
import { requestForToken, onMessageListener } from '../firebaseConfig';
import { useAuth } from './AuthContext';
import { getDeviceInfo } from '../utils/deviceUtils';
import { useNavigate } from 'react-router-dom';
import { encodeId } from '../utils/idEncoder';

const NotificationContext = createContext();

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 20,
        total_pages: 1,
        total_items: 0,
        has_next: false,
        has_prev: false
    });

    const pollingIntervalRef = useRef(null);

    /**
     * Fetch notifications from API
     */
    const fetchNotifications = useCallback(async (options = {}) => {
        const {
            page = 1,
            limit = 20,
            is_read = 'all',
            type = null,
            priority = null,
            silent = false, // Don't show loading state
            append = false  // New option to append data instead of replacing
        } = options;

        if (!silent) {
            setIsLoading(true);
        }
        setError(null);

        try {
            const data = await notificationService.getNotifications({
                page,
                limit,
                is_read,
                type,
                priority,
                sort: 'created_at',
                order: 'desc'
            });

            if (append) {
                setNotifications(prev => {
                    // Filter out duplicates just in case
                    const newIds = new Set(data.notifications.map(n => n.id));
                    const filteredPrev = prev.filter(n => !newIds.has(n.id));
                    return [...filteredPrev, ...data.notifications];
                });
            } else {
                setNotifications(data.notifications);
            }

            setUnreadCount(data.unread_count || 0);
            setPagination(data.pagination || {
                current_page: 1,
                per_page: 20,
                total_pages: 1,
                total_items: 0,
                has_next: false,
                has_prev: false
            });
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
            setError(err.message || 'Failed to load notifications');

            if (!silent) {
                toast.error('Failed to load notifications');
            }
        } finally {
            if (!silent) {
                setIsLoading(false);
            }
        }
    }, []);

    /**
     * Fetch unread count only (for badge)
     */
    const fetchUnreadCount = useCallback(async () => {
        try {
            const data = await notificationService.getUnreadCount();
            // Handle both { unread_count: X } and direct number if API changes
            const count = typeof data === 'object' ? data.unread_count : data;
            setUnreadCount(count || 0);
        } catch (err) {
            // Re-throw 401 to handle in polling loop
            if (err.response && err.response.status === 401) {
                const time = new Date().toLocaleTimeString('th-TH');
                console.warn(`[${time}] ⚠️ Unread Count fetch failed (401). Pushing error to polling handler.`);
                throw err;
            }

                console.error('Failed to fetch unread count:', err);
        }
    }, []);

    /**
     * Mark single notification as read
     */
    const markAsRead = useCallback(async (id) => {
        try {
            await notificationService.markAsRead(id);

            // Update local state optimistically
            setNotifications(prev => prev.map(n =>
                n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Failed to mark notification as read:', err);
            if (err.response?.status !== 401) {
                toast.error('Failed to mark as read');
            }
        }
    }, []);

    /**
     * Mark all notifications as read
     */
    const markAllAsRead = useCallback(async (type = null) => {
        try {
            const data = await notificationService.markAllAsRead(type);

            // Update local state optimistically first
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() })));
            setUnreadCount(0);

            toast.success(`${data.updated_count} notifications marked as read`);

            fetchNotifications({ limit: pagination?.per_page || 20, silent: true });
            fetchUnreadCount();
        } catch (err) {
            console.error('Failed to mark all as read:', err);
            toast.error('Failed to mark all as read');
        }
    }, [fetchNotifications, fetchUnreadCount, pagination?.per_page]);

    /**
     * Delete single notification
     */
    const deleteNotification = useCallback(async (id) => {
        try {
            await notificationService.deleteNotification(id);

            setNotifications(prev => {
                const target = prev.find(n => n.id === id);
                if (target && !target.is_read) {
                    setUnreadCount(c => Math.max(0, c - 1));
                }
                return prev.filter(n => n.id !== id);
            });

            toast.success('Notification deleted');
        } catch (err) {
            console.error('Failed to delete notification:', err);
            toast.error('Failed to delete notification');
        }
    }, []);

    /**
     * Clear all read notifications
     */
    const clearRead = useCallback(async () => {
        try {
            const data = await notificationService.clearReadNotifications();

            // Remove read notifications from local state
            setNotifications(prev => prev.filter(n => !n.is_read));
            toast.success(`${data.deleted_count} read notifications cleared`);
        } catch (err) {
            console.error('Failed to clear read notifications:', err);
            toast.error('Failed to clear read notifications');
        }
    }, []);

    /**
     * Load more notifications (pagination)
     */
    const loadMore = useCallback(async () => {
        if (!pagination?.has_next) return;

        await fetchNotifications({
            page: (pagination?.current_page || 1) + 1,
            limit: pagination?.per_page || 20,
            silent: true,
            append: true
        });
    }, [pagination, fetchNotifications]);

    /**
     * Refresh notifications manually
     */
    const refreshNotifications = useCallback(() => {
        fetchNotifications({ limit: pagination?.per_page || 20 });
        fetchUnreadCount();
    }, [fetchNotifications, fetchUnreadCount, pagination?.per_page]);

    /**
     * Initial fetch on mount
     */
    useEffect(() => {
        if (!isAuthenticated) return;
        fetchNotifications({ limit: 10 }); // Load first 10 notifications
        fetchUnreadCount().catch(() => { }); // Load unread count, ignore errors (handled safely)
    }, [fetchNotifications, fetchUnreadCount, isAuthenticated]);

    /**
     * Smart Polling — polls only when the tab is actively visible.
     *  - Tab visible  → poll every 30 seconds
     *  - Tab hidden   → pause polling (no unnecessary API calls)
     *  - Tab focus    → fetch immediately + restart polling
     *  - Tab visible  → fetch immediately + restart polling
     */
    useEffect(() => {
        if (!isAuthenticated) return;

        const ACTIVE_INTERVAL = 30000;  // 30 วิ เมื่อ user ดูหน้าจออยู่
        const is401 = (err) => err?.response?.status === 401;

        const safeFetch = async () => {
            try {
                await fetchUnreadCount();
            } catch (err) {
                if (is401(err)) {
                    const time = new Date().toLocaleTimeString('th-TH');
                    console.error(`[${time}] 🛑 STOPPING Smart Polling due to 401 Unauthorized.`);
                    stopPolling();
                }
            }
        };

        const stopPolling = () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };

        const startPolling = () => {
            stopPolling(); // ล้าง interval เก่าก่อนเสมอ
            if (document.hidden) return; // ไม่เริ่มถ้า tab ซ่อนอยู่
            pollingIntervalRef.current = setInterval(safeFetch, ACTIVE_INTERVAL);
        };

        // เมื่อ tab กลับมา visible → fetch ทันที + restart interval
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                safeFetch();   // fetch ทันทีโดยไม่รอ interval
                startPolling();
            } else {
                stopPolling(); // หยุด poll เมื่อ tab หาย
            }
        };

        // เมื่อ user focus กลับมาที่ window → fetch ทันที
        const handleFocus = () => {
            safeFetch();
        };

        // เริ่ม polling ตอนแรก
        startPolling();

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            stopPolling();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [fetchUnreadCount, isAuthenticated]);

    /**
     * Setup Firebase Cloud Messaging
     */
    useEffect(() => {
        if (!isAuthenticated) return;
        const setupNotifications = async () => {
            try {
                // 1. Request token (handles permission)
                const token = await requestForToken();
                if (token) {
                    // 2. Get device info (Now async)
                    const { deviceName, deviceType } = await getDeviceInfo();

                    // 3. Send token to backend using new pushTokenService
                    await pushTokenService.registerToken(token, deviceType, deviceName);
                    // console.log('Push token registered successfully for:', deviceName);
                }
            } catch (err) {
                console.error('Failed to setup push notifications:', err);
            }
        };

        setupNotifications();

        // 3. Listen for foreground messages
        const unsubscribe = onMessageListener((payload) => {
            const data = payload.data || {};
            // Omnichannel messages are handled by useChatNotification — skip here
            if (data.type === 'omni_new_message') return;
            const { title, body } = payload.notification || {};

            toast.info(
                <div style={{ cursor: 'pointer' }}>
                    <div className="fw-bold">{title}</div>
                    <div className="small">{body}</div>
                </div>,
                {
                    onClick: () => {
                        refreshNotifications();
                        fetchUnreadCount();

                        if (data.related_id && data.type) {
                            const encodedId = encodeId(data.related_id);
                            switch (data.type) {
                                case 'ticket': navigate(`/tickets/${encodedId}`); break;
                                case 'opportunity': navigate(`/opportunities/${encodedId}`); break;
                                case 'customer': navigate(`/customers/${encodedId}`); break;
                                case 'visit': navigate(`/sales-visits/${encodedId}`); break;
                                case 'task': navigate(`/tasks`); break;
                                default: break;
                            }
                        } else if (data.action_url) {
                            let url = data.action_url;
                            if (url.startsWith('/visits/')) url = url.replace('/visits/', '/sales-visits/');
                            navigate(url);
                        }
                    }
                }
            );
            fetchUnreadCount();
        });

        return () => unsubscribe();
    }, [fetchUnreadCount, refreshNotifications, isAuthenticated, navigate]);



    const value = {
        // State
        notifications,
        unreadCount,
        isLoading,
        error,
        pagination,

        // Actions
        fetchNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearRead,
        loadMore,
        refreshNotifications
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};
